import { useEffect, useState } from 'react'
import AdminLayout from '../../components/shell/AdminLayout'
import EmptyState from '../../components/shell/EmptyState'
import ShiftTemplateForm from '../../components/admin/ShiftTemplateForm'
import RetireCatalogEntryForm from '../../components/admin/RetireCatalogEntryForm'
import { formatClock, useTimeFormat } from '../../lib/timeFormat'
import { getOpenTeams, type AccountTeam } from './accountStore'
import {
  createShiftTemplate,
  getShiftTemplates,
  updateShiftTemplate,
  weekdaysLabel,
  type ShiftTemplate,
  type ShiftTemplateValues,
} from './catalogStore'

type StatusFilter = 'all' | 'active' | 'retired'

const STATUS_FILTERS: { key: StatusFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'retired', label: 'Retired' },
]

// The one form open at a time: creating a template, or acting on one row.
type Panel = { kind: 'create' } | { kind: 'edit' | 'retire'; id: string }

const ACTION =
  'flex min-h-11 items-center justify-center rounded-control border border-line-strong px-3 text-small font-bold hover:bg-surface-2'

/**
 * docs/product/scheduling-model.md's Administrator: manage shift templates.
 * Every template with its hours, meal break, days, and team, and forms to
 * create one, change it, and retire or restore it. A retired template is kept,
 * so what names it still reads, and drops out of the pickers.
 */
export default function AdminShiftTemplatesPage() {
  const { format } = useTimeFormat()
  const [templates, setTemplates] = useState<ShiftTemplate[]>([])
  const [teams, setTeams] = useState<AccountTeam[]>([])
  const [loaded, setLoaded] = useState(false)

  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')

  const [panel, setPanel] = useState<Panel | null>(null)
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    Promise.all([getShiftTemplates(), getOpenTeams()]).then(([loadedTemplates, loadedTeams]) => {
      if (cancelled) return
      setTemplates(loadedTemplates)
      setTeams(loadedTeams)
      setLoaded(true)
    })
    return () => {
      cancelled = true
    }
  }, [])

  const panelTemplate = panel && panel.kind !== 'create' ? (templates.find((template) => template.id === panel.id) ?? null) : null
  const query = search.trim().toLowerCase()
  const filtered = templates.filter((template) => {
    if (statusFilter === 'active' && !template.active) return false
    if (statusFilter === 'retired' && template.active) return false
    return !query || `${template.name} ${template.teamName}`.toLowerCase().includes(query)
  })

  function openPanel(next: Panel) {
    setNotice(null)
    setFormError(null)
    setPanel(next)
  }

  function closePanel() {
    setPanel(null)
    setFormError(null)
  }

  async function run(action: () => Promise<{ ok: true } | { ok: false; message: string }>, done: string) {
    setSaving(true)
    setFormError(null)
    const result = await action()
    setSaving(false)
    if (!result.ok) {
      setFormError(result.message)
      return false
    }
    setTemplates(await getShiftTemplates())
    setPanel(null)
    setNotice(done)
    return true
  }

  function values(template: ShiftTemplate): ShiftTemplateValues {
    return {
      name: template.name,
      startTime: template.startTime,
      endTime: template.endTime,
      mealMinutes: template.mealMinutes,
      weekdays: template.weekdays,
      teamId: template.teamId,
    }
  }

  const create = (next: ShiftTemplateValues) => run(() => createShiftTemplate(next), `Created ${next.name}.`)
  const edit = (next: ShiftTemplateValues) =>
    panelTemplate ? run(() => updateShiftTemplate(panelTemplate.id, next, panelTemplate.active), `Updated ${next.name}.`) : Promise.resolve(false)
  const retire = () => (panelTemplate ? run(() => updateShiftTemplate(panelTemplate.id, values(panelTemplate), false), `Retired ${panelTemplate.name}.`) : Promise.resolve(false))
  const restore = (template: ShiftTemplate) => {
    setNotice(null)
    return run(() => updateShiftTemplate(template.id, values(template), true), `Restored ${template.name}.`)
  }

  return (
    <AdminLayout current="shift-templates" title="Shift templates" subtitle="The hours, meal breaks, and days each shift runs">
      <div className="flex flex-wrap items-center gap-3 rounded-surface border border-line bg-canvas p-3">
        <label htmlFor="shift-search" className="sr-only">
          Search shift templates
        </label>
        <input
          id="shift-search"
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search by name or team"
          className="min-h-11 min-w-[240px] flex-1 rounded-control border border-line-strong px-3 text-small"
        />
        <div role="group" aria-label="Filter by status" className="flex gap-2">
          {STATUS_FILTERS.map((option) => (
            <button
              key={option.key}
              type="button"
              aria-pressed={statusFilter === option.key}
              onClick={() => setStatusFilter(option.key)}
              className={`flex min-h-11 items-center justify-center rounded-control border px-3 text-small font-bold ${
                statusFilter === option.key ? 'border-primary bg-selected' : 'border-line-strong hover:bg-surface-2'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          aria-expanded={panel?.kind === 'create'}
          onClick={() => openPanel({ kind: 'create' })}
          className="flex min-h-11 items-center justify-center rounded-control bg-primary px-4 text-small font-bold text-primary-ink hover:bg-primary-hover"
        >
          Create shift template
        </button>
      </div>

      {notice && (
        <p role="status" className="text-small font-semibold text-success">
          {notice}
        </p>
      )}

      {formError && !panel && (
        <p role="alert" className="text-small font-semibold text-error">
          {formError}
        </p>
      )}

      {panel?.kind === 'create' && <ShiftTemplateForm teams={teams} saving={saving} error={formError} onSave={create} onCancel={closePanel} />}
      {panel?.kind === 'edit' && panelTemplate && (
        <ShiftTemplateForm key={panelTemplate.id} template={panelTemplate} teams={teams} saving={saving} error={formError} onSave={edit} onCancel={closePanel} />
      )}
      {panel?.kind === 'retire' && panelTemplate && (
        <RetireCatalogEntryForm
          key={panelTemplate.id}
          name={panelTemplate.name}
          message={`Retiring ${panelTemplate.name} takes it out of the shift pickers.${
            panelTemplate.placements + panelTemplate.targets > 0
              ? ` ${panelTemplate.placements} ${panelTemplate.placements === 1 ? 'placement' : 'placements'} and ${panelTemplate.targets} staffing ${panelTemplate.targets === 1 ? 'target' : 'targets'} that name it keep reading it.`
              : ' Nothing uses it yet.'
          }`}
          confirmLabel="Retire shift template"
          saving={saving}
          error={formError}
          onConfirm={retire}
          onCancel={closePanel}
        />
      )}

      {!loaded ? (
        <p role="status" className="text-small text-ink-muted">
          Loading&hellip;
        </p>
      ) : filtered.length === 0 ? (
        <EmptyState>No shift templates match your search.</EmptyState>
      ) : (
        <div className="overflow-x-auto rounded-surface border border-line">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                {['Name', 'Hours', 'Meal', 'Days', 'Team', 'Status'].map((heading) => (
                  <th
                    key={heading}
                    scope="col"
                    className="border-b border-line bg-surface-2 px-3 py-2 text-left text-caption font-bold tracking-wide whitespace-nowrap text-ink-subtle uppercase"
                  >
                    {heading}
                  </th>
                ))}
                <th scope="col" className="border-b border-line bg-surface-2 py-2 pr-2 pl-3 text-right">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((template) => (
                <tr key={template.id}>
                  <th scope="row" className="border-b border-line px-3 py-2 text-left text-small font-bold">
                    {template.name}
                  </th>
                  <td className="border-b border-line px-3 py-2 text-small whitespace-nowrap">
                    {formatClock(template.startTime, format)} to {formatClock(template.endTime, format)}
                  </td>
                  <td className="border-b border-line px-3 py-2 text-small whitespace-nowrap text-ink-muted">
                    {template.mealMinutes === 0 ? 'None' : `${template.mealMinutes} min`}
                  </td>
                  <td className="border-b border-line px-3 py-2 text-small whitespace-nowrap">{weekdaysLabel(template.weekdays)}</td>
                  <td className="border-b border-line px-3 py-2 text-small">{template.teamId ? template.teamName : 'Office-wide'}</td>
                  <td className="border-b border-line px-3 py-2 text-small whitespace-nowrap">
                    <span
                      className={`rounded-full px-2 py-0.5 text-caption font-bold ${
                        template.active ? 'bg-success/10 text-success' : 'bg-surface-2 text-ink-muted'
                      }`}
                    >
                      {template.active ? 'Active' : 'Retired'}
                    </span>
                  </td>
                  <td className="border-b border-line py-2 pr-2 pl-3 text-small">
                    {/* Each button has its own fixed slot, so Edit and the
                        retire or restore button line up in every row. */}
                    <div className="flex justify-end gap-2 whitespace-nowrap">
                      <div className="w-14">
                        <button type="button" aria-label={`Edit ${template.name}`} onClick={() => openPanel({ kind: 'edit', id: template.id })} className={`${ACTION} w-full`}>
                          Edit
                        </button>
                      </div>
                      <div className="w-24">
                        {template.active ? (
                          <button type="button" aria-label={`Retire ${template.name}`} onClick={() => openPanel({ kind: 'retire', id: template.id })} className={`${ACTION} w-full`}>
                            Retire
                          </button>
                        ) : (
                          <button type="button" aria-label={`Restore ${template.name}`} disabled={saving} onClick={() => void restore(template)} className={`${ACTION} w-full disabled:opacity-40`}>
                            Restore
                          </button>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  )
}
